import React, { useState, useMemo } from "react";
import { Folder as FolderEntity, List as ListEntity } from "@/entities/all";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Plus, FolderPlus, Search, List as ListIcon, Download, Upload, MoreHorizontal, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { useData } from "../components/DataContext";
import FolderDialog from "../components/dashboard/FolderDialog";
import ProfileImportDialog from "../components/profile/ProfileImportDialog";
import { exportProfile, downloadProfile } from "@/utils/profile";
import { useToast } from "@/components/ui/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import FolderCard from "../components/dashboard/FolderCard";
import ListCard from "../components/dashboard/ListCard";
import AuthLoader from "../components/auth/AuthLoader";
import SignInRequired from "../components/auth/SignInRequired";
import type { Folder, List } from "@/types";

export default function Dashboard() {
  const { folders, lists, sections, listItems, isLoading, runOptimisticUpdate, setOptimisticData, user, userLoading } = useData();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<Folder | null>(null);

  const displayedFolders = useMemo(() => {
    if (!searchQuery) {
      return [...folders].sort((a, b) => (a.position || 0) - (b.position || 0));
    }
    
    const query = searchQuery.toLowerCase();
    return folders
      .filter(folder => {
        if (folder.name?.toLowerCase().includes(query)) return true;
        const folderLists = lists.filter(l => l.folder_id === folder.id);
        return folderLists.some(list => list.title?.toLowerCase().includes(query));
      })
      .sort((a, b) => (a.position || 0) - (b.position || 0));
  }, [folders, lists, searchQuery]);
  
  const getFilteredListsForFolder = (folderId: string) => {
    const folderLists = lists.filter(l => l.folder_id === folderId);
    if (!searchQuery) return folderLists;
    
    const query = searchQuery.toLowerCase();
    const folder = folders.find(f => f.id === folderId);
    
    if (folder?.name?.toLowerCase().includes(query)) {
      return folderLists;
    }
    
    return folderLists.filter(list => list.title?.toLowerCase().includes(query));
  };

  const unorganizedLists = useMemo(() => {
    return lists
      .filter(list => !list.folder_id && list.title?.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => (a.position || 0) - (b.position || 0));
  }, [lists, searchQuery]);

  const handleFolderCreated = async () => {
    setShowFolderDialog(false);
    setEditingFolder(null);
  };

  const handleEditFolder = (folder: Folder) => {
    setEditingFolder(folder);
    setShowFolderDialog(true);
  };

  const handleDeleteFolder = async () => {
    if (!deletingFolder) return;

    const originalState = { folders: [...folders], lists: [...lists] };
    const listsInFolder = lists.filter(l => l.folder_id === deletingFolder.id);
    
    const task = async () => {
      await Promise.all(listsInFolder.map(list => ListEntity.update(list.id, { folder_id: null })));
      await FolderEntity.delete(deletingFolder.id);
    };

    const newState = {
      folders: folders.filter(f => f.id !== deletingFolder.id),
      lists: lists.map(list => 
        listsInFolder.find(l => l.id === list.id) 
          ? { ...list, folder_id: null }
          : list
      ),
      sections: [...sections],
      listItems: [...listItems]
    };

    runOptimisticUpdate(task, { update: newState, originalState: { ...originalState, sections, listItems } });
    setDeletingFolder(null);
  };

  const handleMove = async (id: string, direction: 'up' | 'down', type: 'folder' | 'list') => {
    const items = type === 'folder' ? displayedFolders : unorganizedLists;
    const entity = type === 'folder' ? FolderEntity : ListEntity;
    const entityKey = type === 'folder' ? 'folders' : 'lists';

    const index = items.findIndex(item => item.id === id);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    const originalState = { [entityKey]: [...(type === 'folder' ? folders : lists)] };
    const itemA = items[index];
    const itemB = items[newIndex];
    
    const task = async () => {
      await Promise.all([
        entity.update(itemA.id, { position: newIndex }),
        entity.update(itemB.id, { position: index })
      ]);
    };
    
    const newState = {
      folders: type === 'folder' ? folders.map(item => {
        if (item.id === itemA.id) return { ...item, position: newIndex };
        if (item.id === itemB.id) return { ...item, position: index };
        return item;
      }) : [...folders],
      lists: type === 'list' ? lists.map(item => {
        if (item.id === itemA.id) return { ...item, position: newIndex };
        if (item.id === itemB.id) return { ...item, position: index };
        return item;
      }) : [...lists],
      sections: [...sections],
      listItems: [...listItems]
    };

    setOptimisticData(newState);
    runOptimisticUpdate(task, { update: newState, originalState: { folders, lists, sections, listItems } });
  };
  
  const handleExportProfile = async () => {
    setIsExporting(true);
    try {
      const profile = await exportProfile();
      downloadProfile(profile);
      toast({
        title: "Profile Exported",
        description: "Your profile JSON has been downloaded successfully.",
      });
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: err.message || "Failed to export profile.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (userLoading) {
    return <AuthLoader />;
  }

  if (!user) {
    return <SignInRequired />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-6 mb-6 md:mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              My Lists
            </h1>
            <p className="text-slate-600">Organize your thoughts with beautiful lists</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full md:w-auto">
            <Button
              variant="outline"
              onClick={() => {
                setShowFolderDialog(true);
                setEditingFolder(null);
              }}
              className="flex-1 md:flex-none border-slate-200 hover:bg-slate-50"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              New Folder
            </Button>
            <Link to={createPageUrl("CreateList")} className="flex-1 md:flex-none">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 shadow-lg">
                <Plus className="w-4 h-4 mr-2" />
                New List
              </Button>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="border-slate-200 hover:bg-slate-50 shrink-0" title="More options">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={handleExportProfile}
                  disabled={isExporting}
                  className="cursor-pointer"
                >
                  {isExporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin text-blue-500" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowImportDialog(true)}
                  className="cursor-pointer"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Profile
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6 md:mb-8">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search lists and folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 bg-white border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Content */}
        <div className="space-y-8 md:space-y-12">
          {/* Folders */}
          {displayedFolders.length > 0 && (
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-6">Folders</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <AnimatePresence>
                  {displayedFolders.map((folder, index) => (
                    <motion.div
                      key={folder.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <FolderCard 
                        folder={folder} 
                        lists={getFilteredListsForFolder(folder.id)}
                        onEdit={() => handleEditFolder(folder)}
                        onDelete={() => setDeletingFolder(folder)}
                        onMove={(direction) => handleMove(folder.id, direction, 'folder')}
                        index={index}
                        totalItems={displayedFolders.length}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </section>
          )}

          {/* Unorganized Lists */}
          {unorganizedLists.length > 0 && (
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-6">Lists</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence>
                  {unorganizedLists.map((list, index) => (
                    <motion.div
                      key={list.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <ListCard 
                        list={list} 
                        onMove={(direction) => handleMove(list.id, direction, 'list')}
                        index={index}
                        totalItems={unorganizedLists.length}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </section>
          )}

          {/* Empty State */}
          {!isLoading && displayedFolders.length === 0 && unorganizedLists.length === 0 && (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <ListIcon className="w-12 h-12 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                {searchQuery ? 'No results found' : 'No lists yet'}
              </h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                {searchQuery 
                  ? 'Try adjusting your search terms'
                  : 'Create your first list to get started organizing your thoughts'
                }
              </p>
              {!searchQuery && (
                <Link to={createPageUrl("CreateList")}>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Create First List
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      <FolderDialog
        open={showFolderDialog}
        folder={editingFolder}
        onClose={() => {
          setShowFolderDialog(false);
          setEditingFolder(null);
        }}
        onSaved={handleFolderCreated}
      />

      <ProfileImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
      />

      <AlertDialog open={!!deletingFolder} onOpenChange={() => setDeletingFolder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the folder "{deletingFolder?.name}". Lists inside this folder will not be deleted but will become unorganized. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFolder} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
